import { ILessonAlert } from 'app/components/call/BookingDetails/LessonBookingDetails/LessonAlert';
import { ButtonDirective } from 'app/components/call/BookingDetails/LessonBookingDetails/LessonOutcome/Button/ButtonDirective';
import { AlertVariant } from 'app/components/presentation/alert/AlertBox';
import { ColorScheme } from 'app/components/presentation/buttons/ButtonClassNameA';
import SvgBeachChairOwlLookingUp from 'app/components/svgs/mascot/BeachChairOwlLookingUp';
import SvgStressedOutOwl from 'app/components/svgs/mascot/StressedOutOwl';
import SvgThumbsUp from 'app/components/svgs/mascot/ThumbsUp';
import SvgSadSlightly from 'app/components/svgs/SadSlightly';
import SvgSorry from 'app/components/svgs/Sorry';
import {
  AUTOMATIC_ABSENCE,
  AUTOMATIC_PARTIAL_ABSENCE,
  LATE_MANUAL,
  TEACHER_RECORDED_ABSENCE,
  STAFF_RECORDED_ABSENCE,
} from 'app/constants/enums/CancellationReason';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { CallOutcome, CallOutcomeType } from 'app/containers/call/CallOutcome';
import { tFunctionType } from 'app/services/localization';
import { ComponentType, SVGProps } from 'react';

// The frontend does not store these concerns, but need to be conveyed to the student.
// If these change frequently, they may be good candidates for a configuration.
const STUDENT_DISCONNECTED_TIME_COUNT = 15;
const STUDENT_LATE_TIME_COUNT = 15;

interface IButton {
  colorScheme?: ColorScheme;
  directive: ButtonDirective;
}

interface ILessonModal {
  title: string;
}

/**
 * Messaging required for the end of the lesson modal, end lesson summary
 * and abridged booking details.
 */
export interface ILessonOutcomeMessaging {
  alert: ILessonAlert | null;
  asset: ComponentType<SVGProps<SVGSVGElement>>;
  buttons: IButton[];
  modal: ILessonModal | null;
  status: {
    abridged: string;
    summary: string;
  };
}

const DASHBOARD_BOOKING_BUTTONS: IButton[] = [
  {
    colorScheme: ColorScheme.Secondary,
    directive: ButtonDirective.Dashboard,
  },
  {
    directive: ButtonDirective.BookLesson,
  },
];

const BOOKING_ONLY_BUTTON: IButton[] = [{ directive: ButtonDirective.BookLesson }];
const TUTOR_DASHBOARD_ONLY_BUTTON: IButton[] = [{ directive: ButtonDirective.TutorDashboard }];

/**
 * Helper to get the correct messaging if the user is a student.
 */
const createStudentLessonOutcomeMessaging = (t: tFunctionType, outcome: CallOutcome): ILessonOutcomeMessaging => {
  switch (outcome.type) {
    // Lesson time out.
    case CallOutcomeType.TimedOut: {
      return {
        alert: null,
        asset: SvgThumbsUp,
        buttons: BOOKING_ONLY_BUTTON,
        modal: null,
        status: {
          abridged: t('lesson-outcome.student.timed-out.status.abridged'),
          summary: t('lesson-outcome.student.timed-out.status.summary'),
        },
      };
    }

    // Lesson complete.
    case CallOutcomeType.Complete: {
      return {
        alert: null,
        asset: SvgThumbsUp,
        buttons: BOOKING_ONLY_BUTTON,
        modal: null,
        status: {
          abridged: t('lesson-outcome.student.complete.status.abridged'),
          summary: t('lesson-outcome.student.complete.status.summary'),
        },
      };
    }

    // Cancellation event.
    case CallOutcomeType.Cancelled: {
      switch (outcome.cancellationType.id) {
        case 'administrative': {
          return {
            alert: null,
            asset: SvgSorry,
            buttons: DASHBOARD_BOOKING_BUTTONS,
            modal: {
              title: t('lesson-outcome.student.cancellation.staff.default.modal.title'),
            },
            status: {
              abridged: t('lesson-outcome.student.cancellation.staff.default.status.abridged'),
              summary: t('lesson-outcome.student.cancellation.staff.default.status.summary'),
            },
          };
        }

        case 'student': {
          switch (outcome.cancellationReason) {
            case AUTOMATIC_ABSENCE: {
              return {
                alert: null,
                asset: SvgStressedOutOwl,
                buttons: BOOKING_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.student.cancellation.student.automatic-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.student.automatic-absence.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.student.automatic-absence.status.summary', {
                    count: STUDENT_LATE_TIME_COUNT,
                  }),
                },
              };
            }

            case AUTOMATIC_PARTIAL_ABSENCE: {
              return {
                alert: null,
                asset: SvgStressedOutOwl,
                buttons: BOOKING_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.student.cancellation.student.automatic-partial-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.student.automatic-partial-absence.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.student.automatic-partial-absence.status.summary', {
                    count: STUDENT_DISCONNECTED_TIME_COUNT,
                  }),
                },
              };
            }

            case TEACHER_RECORDED_ABSENCE: {
              return {
                alert: null,
                asset: SvgStressedOutOwl,
                buttons: BOOKING_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.student.cancellation.student.teacher-recorded-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.student.teacher-recorded-absence.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.student.teacher-recorded-absence.status.summary', {
                    count: STUDENT_DISCONNECTED_TIME_COUNT,
                  }),
                },
              };
            }

            default: {
              return {
                alert: null,
                asset: SvgSadSlightly,
                buttons: BOOKING_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.student.cancellation.student.default.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.student.default.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.student.default.status.summary'),
                },
              };
            }
          }
        }

        case 'teacher': {
          switch (outcome.cancellationReason) {
            case AUTOMATIC_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.student.cancellation.teacher.automatic-absence.alert.message'),
                },
                asset: SvgSorry,
                buttons: DASHBOARD_BOOKING_BUTTONS,
                modal: {
                  title: t('lesson-outcome.student.cancellation.teacher.automatic-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.teacher.automatic-absence.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.teacher.automatic-absence.status.summary'),
                },
              };
            }

            case AUTOMATIC_PARTIAL_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.student.cancellation.teacher.automatic-partial-absence.alert.message'),
                },
                asset: SvgSorry,
                buttons: DASHBOARD_BOOKING_BUTTONS,
                modal: {
                  title: t('lesson-outcome.student.cancellation.teacher.automatic-partial-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.teacher.automatic-partial-absence.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.teacher.automatic-partial-absence.status.summary'),
                },
              };
            }

            case STAFF_RECORDED_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.student.cancellation.teacher.staff-recorded-absence.alert.message'),
                },
                asset: SvgSorry,
                buttons: DASHBOARD_BOOKING_BUTTONS,
                modal: {
                  title: t('lesson-outcome.student.cancellation.teacher.staff-recorded-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.teacher.staff-recorded-absence.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.teacher.staff-recorded-absence.status.summary'),
                },
              };
            }

            default: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.student.cancellation.teacher.default.alert.message'),
                },
                asset: SvgSorry,
                buttons: DASHBOARD_BOOKING_BUTTONS,
                modal: {
                  title: t('lesson-outcome.student.cancellation.teacher.default.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.student.cancellation.teacher.default.status.abridged'),
                  summary: t('lesson-outcome.student.cancellation.teacher.default.status.summary'),
                },
              };
            }
          }
        }
      }
    }
  }
};

/**
 * Helper to get the correct messaging if the user is a student.
 */
const createTeacherLessonOutcomeMessaging = (t: tFunctionType, outcome: CallOutcome): ILessonOutcomeMessaging => {
  switch (outcome.type) {
    // Lesson time out.
    case CallOutcomeType.TimedOut: {
      return {
        alert: null,
        asset: SvgThumbsUp,
        buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
        modal: null,
        status: {
          abridged: t('lesson-outcome.teacher.timed-out.status.abridged'),
          summary: t('lesson-outcome.teacher.timed-out.status.summary'),
        },
      };
    }

    // Lesson complete.
    case CallOutcomeType.Complete: {
      return {
        alert: null,
        asset: SvgThumbsUp,
        buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
        modal: null,
        status: {
          abridged: t('lesson-outcome.teacher.complete.status.abridged'),
          summary: t('lesson-outcome.teacher.complete.status.summary'),
        },
      };
    }

    // Lesson cancellation.
    case CallOutcomeType.Cancelled: {
      switch (outcome.cancellationType.id) {
        case 'administrative': {
          return {
            alert: null,
            asset: SvgSorry,
            buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
            modal: {
              title: t('lesson-outcome.teacher.cancellation.staff.default.modal.title'),
            },
            status: {
              abridged: t('lesson-outcome.teacher.cancellation.staff.default.status.abridged'),
              summary: t('lesson-outcome.teacher.cancellation.staff.default.status.summary'),
            },
          };
        }

        case 'student': {
          switch (outcome.cancellationReason) {
            case AUTOMATIC_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.teacher.cancellation.student.automatic-absence.alert.message'),
                },
                asset: SvgBeachChairOwlLookingUp,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.student.automatic-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.student.automatic-absence.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.student.automatic-absence.status.summary'),
                },
              };
            }

            case AUTOMATIC_PARTIAL_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.teacher.cancellation.student.automatic-partial-absence.alert.message'),
                },
                asset: SvgBeachChairOwlLookingUp,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.student.automatic-partial-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.student.automatic-partial-absence.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.student.automatic-partial-absence.status.summary'),
                },
              };
            }

            case LATE_MANUAL: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.teacher.cancellation.student.late-manual.alert.message'),
                },
                asset: SvgBeachChairOwlLookingUp,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.student.late-manual.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.student.late-manual.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.student.late-manual.status.summary'),
                },
              };
            }

            case TEACHER_RECORDED_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Info,
                  message: t('lesson-outcome.teacher.cancellation.student.teacher-recorded-absence.alert.message'),
                },
                asset: SvgBeachChairOwlLookingUp,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.student.teacher-recorded-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.student.teacher-recorded-absence.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.student.teacher-recorded-absence.status.summary'),
                },
              };
            }

            default: {
              return {
                alert: null,
                asset: SvgSorry,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.student.default.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.student.default.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.student.default.status.summary'),
                },
              };
            }
          }
        }

        case 'teacher': {
          switch (outcome.cancellationReason) {
            case AUTOMATIC_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Warning,
                  message: t('lesson-outcome.teacher.cancellation.teacher.automatic-absence.alert.message'),
                },
                asset: SvgSadSlightly,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.teacher.automatic-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.teacher.automatic-absence.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.teacher.automatic-absence.status.summary'),
                },
              };
            }

            case AUTOMATIC_PARTIAL_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Warning,
                  message: t('lesson-outcome.teacher.cancellation.teacher.automatic-partial-absence.alert.message'),
                },
                asset: SvgStressedOutOwl,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.teacher.automatic-partial-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.teacher.automatic-partial-absence.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.teacher.automatic-partial-absence.status.summary'),
                },
              };
            }

            case STAFF_RECORDED_ABSENCE: {
              return {
                alert: {
                  alertVariant: AlertVariant.Warning,
                  message: t('lesson-outcome.teacher.cancellation.teacher.staff-recorded-absence.alert.message'),
                },
                asset: SvgStressedOutOwl,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.teacher.staff-recorded-absence.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.teacher.staff-recorded-absence.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.teacher.staff-recorded-absence.status.summary'),
                },
              };
            }

            default: {
              return {
                alert: null,
                asset: SvgStressedOutOwl,
                buttons: TUTOR_DASHBOARD_ONLY_BUTTON,
                modal: {
                  title: t('lesson-outcome.teacher.cancellation.teacher.default.modal.title'),
                },
                status: {
                  abridged: t('lesson-outcome.teacher.cancellation.teacher.default.status.abridged'),
                  summary: t('lesson-outcome.teacher.cancellation.teacher.default.status.summary'),
                },
              };
            }
          }
        }
      }
    }
  }
};

/**
 * Retrieves the appropriate messages to show users at the end of their lesson.
 */
export const createLessonOutcomeMessaging = (
  t: tFunctionType,
  lessonBookingRole: LessonBookingRole.Student | LessonBookingRole.Teacher,
  outcome: CallOutcome,
): ILessonOutcomeMessaging | null => {
  switch (lessonBookingRole) {
    case LessonBookingRole.Student:
      return createStudentLessonOutcomeMessaging(t, outcome);
    case LessonBookingRole.Teacher:
      return createTeacherLessonOutcomeMessaging(t, outcome);
  }
};
